import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Variables } from '../base';
import { TitleCard } from '../card';
import { DataTable } from '../data-table';
import { Icon } from '@mdi/react';
import MarkdownWrapper from '../base/markdown-wrapper/markdown-wrapper';

interface ISelectableProps {
    selectable: boolean;
}

interface IAlign {
    align?: 'left' | 'right';
}
interface IExpandParent {
    expand?: boolean;
}

interface ICell {
    colSpan?: number;
}

const hoverable = css`
    &:hover {
        background-color: ${Variables.Colors.p50};
        circle.dot {
            fill: ${Variables.Colors.b300};
        }
        circle.open {
            fill: ${Variables.Colors.white};
            stroke: ${Variables.Colors.b300};
        }
        line.fg {
            stroke: ${Variables.Colors.b300};
        }
        line.bg {
            stroke: ${Variables.Colors.p50};
        }
    }
`;

const selectable = (props: ISelectableProps) => css`
    display: block;

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    cursor: ${props.selectable ? 'pointer' : 'default'};
`;

export const SelectableCell = styled.span<ISelectableProps>`
    ${selectable};
    padding: 8px 4px;
    line-height: 24px;
    height: 40px;
`;

export const StyledTitleCard = styled(TitleCard)`
    & > section > section:first-of-type {
        display: none;
    }
`;

export const StyledDataTable = styled(DataTable)`
    display: flex;
    flex-direction: column;
    margin: 12px 0;
    min-height: 200px;

    & > div {
        overflow-y: hidden;
    }

    div[role='table'] > div:first-of-type {
        [role='columnheader'] {
            white-space: nowrap;
        }
    }

    div[role='row'] {
        ${hoverable}
    }

    div[role='columnheader'] {
        padding: 8px 4px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    div[role='cell'] {
        display: block;
        font-size: 15px;
        color: ${Variables.Colors.n70};
        padding: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

export const StyledSegment = styled(SelectableCell)`
    ${selectable};
    padding: 0;
`;

export const StyledEvent = styled.span`
    color: ${Variables.Colors.n100};
    background-color: ${Variables.Colors.white};
    border: 1px solid ${Variables.Colors.platinum};
    border-radius: ${Variables.Rounded.medium};
    padding: 2px 6px;

    &:not(:first-of-type) {
        margin-left: 4px;
    }
`;
export const StyledDescription = styled(SelectableCell)`
    color: ${Variables.Colors.n100};
    width: 100%;
`;
export const Description = styled.span``;
export const DescriptionItem = styled.span`
    &:not(:first-of-type) {
        margin-left: 8px;
    }
`;
export const ExpandRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: stretch;
    padding: 0;
    width: 100%;
`;
export const ExpandSegment = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 50px;
`;
export const ExpandCell = styled.div`
    display: flex;
    flex-grow: 1;
    padding: 16px 0px;
`;
export const ExpandCol = styled.div`
    /* display: flex;
    flex-direction: column;
    flex-grow: 1; */
    width: 100%;
`;

export const ExpandWrap = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    flex-grow: 1;
`;

export const ExpandContainer = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
`;

export const ExpandTable = styled.table<IExpandParent>`
    white-space: normal;
    color: ${Variables.Colors.n100};

    ${({ expand }) =>
        expand
            ? css`
                  margin: 0;
              `
            : css`
                  min-width: 40%;
              `}
`;

export const ExpandTD = styled.td<IAlign>`
    text-align: ${(props: IAlign): string => (props.align ? props.align : 'left')};
`;
export const ExpandTH = styled.th<IAlign>`
    text-align: ${(props: IAlign): string => (props.align ? props.align : 'left')};
    vertical-align: top;
    font-weight: 500;
`;
export const ExpandTR = styled.tr`
    & ${ExpandTD}, & ${ExpandTH} {
        font-size: 16px;
        letter-spacing: 0.5px;
        padding: 4px 8px;

        overflow: hidden;
        text-overflow: ellipsis;
        /* white-space: nowrap; */
    }
`;

export const ExpandTHead = styled.thead`
    ${ExpandTH} {
        font-size: 20px;
        padding-bottom: 16px;
    }
`;
export const ExpandTBody = styled.tbody``;
export const ExpandTFoot = styled.tfoot``;

export const ExpandSubTH = styled(ExpandTH)``;
export const ExpandTitleTH = styled(ExpandTH)``;

export const ExpandTitle = styled.h3`
    margin: 18px 0 0 0;
    font-weight: 500;
    font-size: 16px;
    text-decoration: underline;
`;

export const StyledLink = styled.a`
    color: ${Variables.Colors.p300};
    text-decoration: underline;
`;
export const StyledIcon = styled(Icon)`
    margin-top: 4px;
    height: 16px;
    width: 16px;
    fill: ${Variables.Colors.b300};
    align-self: center;
`;
export const Attachment = styled.span`
    display: inline-flex;
    align-items: center;
`;
export const AttachIcon = styled(Icon)`
    transform: rotate(45deg);
    margin-right: 4px;
    fill: ${Variables.Colors.n30};
`;
export const MarkdownBlock = styled(MarkdownWrapper)`
    margin: 0;
`;
export const DocumentIcon = styled(Icon)`
    height: 64px;
    width: 64px;
    fill: ${Variables.Colors.n50};
    margin-right: 32px;
    cursor: pointer;
`;

export const ClaimBundleWrapper = styled.div`
    display: flex;
    padding-left: 16px;
    margin: 10px;
`;

export const ExpandLabel = styled.div`
    font-weight: 500;
    padding: 5px 0;
`;

export const Divider = styled.div`
    width: 100%;
    height: 1px;
    background: ${Variables.Colors.platinum};
    margin: 16px 0;
    position: relative;
`;

export const RowGroupWrapper = styled.div`
    text-align: center;
    background: ${Variables.Colors.n15_48};
    padding: 10px 0;
    cursor: pointer;
    user-select: none;
    border-top: 1px solid ${Variables.Colors.n15};
    border-bottom: 1px solid ${Variables.Colors.n15};

    &:hover {
        background: ${Variables.Colors.n15_16};
    }
`;

export const Wrapper = styled.div`
    width: 100%;
`;
